import {
    Combobox,
    Transition,
    ComboboxInput,
    ComboboxButton,
    ComboboxOptions,
    ComboboxOption,
    Field,
} from '@headlessui/react';
import Icon from 'flow/components/ui-components/Icon';
import useExtension from 'flow/hooks/useExtension';
import { ExtensionKey } from 'flow/types/EntityKeys';
import { MobileStatus } from 'flow/types/Extension';
import { SmsSenderNameOption } from 'flow/types/Profile';

import { TypedMap } from 'flow/types/TypedMap';
import { NumberState } from 'flow/utils/extension-utils';
import { twMerge } from 'tailwind-merge';
import { List, Map } from 'immutable';
import React, { Fragment, useState } from 'react';
import { useAppSelector } from 'flow/hooks/useRedux';

export interface SmsNumberOption {
    id: string;
    name: string;
    disabled?: boolean;
}

interface Props {
    /**
     * Names the field for assistive tech. The visible label sits outside this
     * component, so only the caller can say what this combobox is for.
     */
    ariaLabel?: string;
    extensionKey: ExtensionKey;
    caller: SmsNumberOption;
    onChange: (newValue: SmsNumberOption) => void;
    editable: boolean;
}

const SmsNumberCombobox = ({
    extensionKey,
    ariaLabel,
    caller,
    onChange,
    editable,
}: Props) => {
    const { extension } = useExtension(extensionKey);

    const smsSenderNameOptions =
        (useAppSelector(
            (state) =>
                state.profiles.get('smsSenderNameOptions', List()) as TypedMap<
                    Record<string, TypedMap<SmsSenderNameOption>>
                >,
        )
            .sortBy(
                (value, key) => key,
                (optionA, optionB) => (optionA < optionB ? -1 : 1),
            )
            .map((option) => ({
                id: option.get('key'),
                name: option.get('description'),
                disabled: false,
            }))
            .toArray() as SmsNumberOption[]) || [];

    const mobileStatus = extension.get(
        'mobileStatus',
        Map(),
    ) as TypedMap<MobileStatus>;

    const defaultOptions: SmsNumberOption[] = smsSenderNameOptions.concat(
        mobileStatus.get('mobileNumberState') === NumberState.nonverified.key
            ? [
                  {
                      id: mobileStatus.getIn([
                          'number',
                          'prettyNationalNumber',
                      ]),
                      name: mobileStatus.getIn([
                          'number',
                          'prettyNationalNumber',
                      ]),
                      disabled: true,
                  },
              ]
            : [],
    );

    const [query, setQuery] = useState('');

    const filteredCallIds =
        query === ''
            ? defaultOptions
            : defaultOptions.filter((option) =>
                  option.name
                      .toLowerCase()
                      .replace(/\s+/g, '')
                      .includes(query.toLowerCase().replace(/\s+/g, '')),
              );

    return (
        <Field disabled={!editable}>
            <Combobox value={caller} by="id" onChange={onChange}>
                <div className="relative w-full text-sm/4">
                    <ComboboxInput
                        aria-label={ariaLabel}
                        className="focus-ring w-full rounded-sm border-1 bg-gray-200 py-3 ps-3 pe-9 text-gray-900"
                        displayValue={(option: SmsNumberOption) => option.name}
                        onChange={(event) => setQuery(event.target.value)}
                    />

                    <ComboboxButton className="absolute inset-y-0 end-0 flex items-center pe-2">
                        <Icon
                            className="size-5 text-gray-600"
                            iconKey="arrow_down"
                            aria-hidden="true"
                        />
                    </ComboboxButton>

                    <Transition
                        as={Fragment}
                        leave="transition ease-in duration-100"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                        afterLeave={() => setQuery('')}
                    >
                        <ComboboxOptions className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black/5 focus:outline-hidden">
                            {filteredCallIds.map((option) => (
                                <ComboboxOption
                                    key={option.id}
                                    disabled={option.disabled}
                                    className={({ focus }) =>
                                        twMerge(
                                            'relative cursor-default py-2 ps-3 pe-9 select-none',
                                            focus
                                                ? 'bg-gray-200'
                                                : 'text-gray-900',
                                        )
                                    }
                                    value={option}
                                >
                                    {({ selected }) => (
                                        <span
                                            className={twMerge(
                                                'truncate text-gray-500',
                                                selected && 'font-medium',
                                            )}
                                        >
                                            {option.name}
                                        </span>
                                    )}
                                </ComboboxOption>
                            ))}
                        </ComboboxOptions>
                    </Transition>
                </div>
            </Combobox>
        </Field>
    );
};

export default SmsNumberCombobox;
